"use client";

import { useEffect, useRef } from "react";
import Link from "next/link";
import Icon from "@/components/ui/AppIcon";

export default function HeroSection() {
  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = heroRef.current;
    if (!el) return;
    const items = el.querySelectorAll(".hero-reveal");
    items.forEach((item, i) => {
      (item as HTMLElement).style.animationDelay = `${0.15 + i * 0.12}s`;
      item.classList.add("animate-in");
    });
  }, []);

  return (
    <section
      ref={heroRef}
      className="relative min-h-screen flex items-center pt-20 overflow-hidden"
    >
      {/* Background layers */}
      <div className="absolute inset-0 bg-[#0C1B33]" />
      <div
        className="absolute inset-0 opacity-30"
        style={{
          backgroundImage: `radial-gradient(ellipse 80% 60% at 60% 40%, rgba(201,168,76,0.12) 0%, transparent 70%)`,
        }}
      />
      {/* Grid lines */}
      <div
        className="absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage: `linear-gradient(rgba(245,240,232,1) 1px, transparent 1px), linear-gradient(90deg, rgba(245,240,232,1) 1px, transparent 1px)`,
          backgroundSize: "80px 80px",
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-6 w-full">
        <div className="grid lg:grid-cols-12 gap-12 items-center min-h-[calc(100vh-80px)] py-20">
          {/* Left: Text */}
          <div className="lg:col-span-6 space-y-8">
            {/* Badge */}
            <div className="hero-reveal opacity-0 inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[rgba(184,134,11,0.4)] bg-[rgba(184,134,11,0.1)]">
              <span className="w-1.5 h-1.5 rounded-full bg-gold pulse-glow" />
              <span className="text-[11px] font-display font-600 uppercase tracking-[0.18em] text-gold">
                Global Operations Partner
              </span>
            </div>

            {/* Headline */}
            <h1 className="hero-reveal opacity-0 font-display font-700 leading-[1.0] tracking-tight">
              <span className="block text-5xl md:text-6xl lg:text-7xl text-cream">
                Optimizing
              </span>
              <span className="block text-5xl md:text-6xl lg:text-7xl text-shimmer mt-1">
                Global Operations.
              </span>
              <span className="block text-4xl md:text-5xl lg:text-6xl text-cream/70 font-300 mt-2">
                Delivering Measurable Growth.
              </span>
            </h1>

            {/* Subheadline */}
            <p className="hero-reveal opacity-0 text-[20px] leading-[1.75] text-cream/80 font-body font-500 max-w-xl">
              A trusted partner in asset management, logistics, transportation, and strategic business consulting — helping businesses streamline operations and maximize efficiency worldwide.
            </p>

            {/* CTAs */}
            <div className="hero-reveal opacity-0 flex flex-col sm:flex-row gap-4">
              <Link
                href="/homepage#contact"
                className="btn-primary px-8 py-4 text-sm rounded-sm text-center font-display"
              >
                Get a Consultation
              </Link>
              <Link
                href="/homepage#services"
                className="btn-outline px-8 py-4 text-sm rounded-sm text-center font-display"
              >
                Explore Our Services
              </Link>
            </div>

            {/* Trust indicators */}
            <div className="hero-reveal opacity-0 flex items-center gap-6 pt-2">
              {[
                { icon: "GlobeAltIcon", label: "Global Reach" },
                { icon: "ChartBarIcon", label: "Data-Driven" },
                { icon: "ShieldCheckIcon", label: "Trusted Partner" },
              ].map((item) => (
                <div key={item.label} className="flex items-center gap-2">
                  <Icon name={item.icon as "GlobeAltIcon"} size={16} className="text-gold" />
                  <span className="text-[14px] font-display font-600 uppercase tracking-wider text-cream/75">
                    {item.label}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Floating Cards */}
          <div className="lg:col-span-6 relative h-[520px] hidden lg:block">
            {/* Main analytics card */}
            <div
              className="float-a absolute top-8 right-0 w-72 glass-card rounded-2xl p-6 shadow-[0_24px_80px_rgba(0,0,0,0.5)]"
              style={{ zIndex: 3 }}
            >
              <div className="flex items-center justify-between mb-4">
                <span className="text-[10px] font-display uppercase tracking-widest text-muted">
                  Operational Efficiency
                </span>
                <span className="text-[10px] bg-[rgba(184,134,11,0.2)] text-gold px-2 py-0.5 rounded-full font-display uppercase tracking-wider">
                  Live
                </span>
              </div>
              <div className="text-4xl font-display font-700 text-gold mb-1">
                +38%
              </div>
              <div className="text-[14px] text-cream/60 font-body mb-4">
                Cost reduction avg. across clients
              </div>
              {/* Mini bar chart */}
              <div className="flex items-end gap-1.5 h-12">
                {[40, 55, 45, 70, 60, 85, 78, 92].map((h, i) => (
                  <div
                    key={i}
                    className="flex-1 rounded-sm"
                    style={{
                      height: `${h}%`,
                      background:
                        i === 7
                          ? "var(--gold)"
                          : "rgba(184,134,11,0.3)",
                    }}
                  />
                ))}
              </div>
            </div>

            {/* Second floating card */}
            <div
              className="float-b absolute top-1/2 left-0 w-60 glass-card rounded-xl p-5 shadow-[0_16px_60px_rgba(0,0,0,0.4)]"
              style={{ zIndex: 2, marginTop: "-60px" }}
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 rounded-lg bg-[rgba(184,134,11,0.18)] flex items-center justify-center">
                  <Icon name="TruckIcon" size={18} className="text-gold" />
                </div>
                <div>
                  <div className="text-[11px] font-display uppercase tracking-widest text-muted">
                    Logistics
                  </div>
                  <div className="text-[16px] font-display font-700 text-cream">
                    On-Time Rate
                  </div>
                </div>
              </div>
              <div className="text-3xl font-display font-700 text-cream mb-1">
                97.4%
              </div>
              <div className="flex items-center gap-1.5">
                <Icon name="ArrowTrendingUpIcon" size={14} className="text-emerald-400" />
                <span className="text-[12px] text-emerald-400 font-display">
                  +4.2% this quarter
                </span>
              </div>
            </div>

            {/* Third floating card — industries */}
            <div
              className="float-c absolute bottom-8 right-12 w-56 glass-card rounded-xl p-5 shadow-[0_16px_60px_rgba(0,0,0,0.35)]"
              style={{ zIndex: 2 }}
            >
              <div className="text-[11px] font-display uppercase tracking-widest text-muted mb-3">
                Industries Served
              </div>
              <div className="space-y-2">
                {["Manufacturing", "Retail & E-comm", "Import / Export"].map((ind) => (
                  <div key={ind} className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-gold opacity-70" />
                    <span className="text-[13px] font-body text-cream/70">{ind}</span>
                  </div>
                ))}
                <div className="text-[12px] font-display text-gold pt-1">
                  + 2 more sectors →
                </div>
              </div>
            </div>

            {/* Decorative glow */}
            <div
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 h-80 rounded-full pointer-events-none"
              style={{
                background:
                  "radial-gradient(ellipse, rgba(201,168,76,0.08) 0%, transparent 70%)",
              }}
            />
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 opacity-40">
        <span className="text-[10px] font-display uppercase tracking-widest text-cream">
          Scroll
        </span>
        <div className="w-px h-10 bg-gradient-to-b from-gold to-transparent" />
      </div>
    </section>
  );
}